import React, {useCallback, useEffect, useMemo, useState} from "react";
import Dialog from "../../base/dialog";
import {DialogActions, DialogContent, DialogContentText, DialogTitle, MenuItem} from "@mui/material";
import Button from "../../base/button/button";
import Grid2 from "@mui/material/Unstable_Grid2";
import Form, {MuiRFF, Yup} from "../../form";
import ValidateMessages from "../../../../core/models/static/texts/vallidate-messages";
import ExternalPartAutocomplete from "../../text-fields/autocomplete/part-autocomplete/external-part";
import useIndustry from "../../../hooks/use-industry";
import LoadingLottieAnimation from "../../app-specific/lottie-animations/loading";
import Api from "../../../../core/services/api/api";
import ApiResponseCodes from "../../../../core/models/static/api-response-codes";
import useIsMounted from "../../../hooks/use-is-mounted";


const ImportExternalProductDialog = ({open, setOpen, onSuccess}) => {
    const [importing, setImporting] = useState(false);
    const {
        industries,
        cachedIndustry,
        fetchIndustry,
        fetchedIndustry,
        loadingIndustries,
        hasNoIndustries,
    } = useIndustry({noCategories: true});
    const isMounted = useIsMounted();
    const schema = React.useMemo(() => Yup.object().shape({
        industry: Yup.mixed().nullable().required(ValidateMessages.required),
        externalProduct: Yup.mixed().required(ValidateMessages.required),
    }), []);

    const validate = React.useMemo(() => MuiRFF.makeValidate(schema), [schema]);
    const required = React.useMemo(() => MuiRFF.makeRequired(schema), [schema]);

    const initialValues = useMemo(() => {
        return {
            industry: fetchedIndustry ?? cachedIndustry ?? null,

        }
    }, [cachedIndustry, fetchedIndustry]);

    /**
     * Automatically fetches the cached industry when the component is mounted
     */
    useEffect(() => {
        if (!cachedIndustry?.id)
            return;
        void fetchIndustry({industryId: cachedIndustry.id});
    }, [cachedIndustry?.id, fetchIndustry]);

    /**
     * Automatically fetches the industry if there is only one industry available and it's not already fetched or selected
     */
    useEffect(() => {
        if (industries?.length === 1) {
            const industry = industries[0];
            void fetchIndustry({industryId: industry.id});
        }
    }, [fetchIndustry, industries]);

    /**
     * Handles the form submission.
     * @type {(function(*): Promise<void>)}
     */
    const submitForm = useCallback(async (values) => {
        setImporting(true);
        const request = {
            industryId: values.industry?.id,
            internalId: values.externalProduct?.internalId,
        };
        let response = await Api.importPartFromExternal(request, true)
        if (response?.errorCode === ApiResponseCodes.defaultCategoryRequiresForIndustry) {
            // create a default category for the request.industryId
            const categoryResponse = await Api.createPartCategory({
                title: "Default",
                industryId: request.industryId,
            })
            if (categoryResponse?.resultFlag) {
                response = await Api.importPartFromExternal(request);
            }
        }
        if (!isMounted())
            return;
        setImporting(false);
        if (response?.resultFlag) {
            if (onSuccess) {
                onSuccess(response.data);
            }
            setOpen(false);
        }
    }, [onSuccess, setOpen, isMounted])

    return (
        <>
            <Dialog
                open={open}
                setOpen={setOpen}
                className={'new-product-dialog'}
                maxWidth={'sm'}
            >
                <DialogTitle>
                    Import External Product
                </DialogTitle>
                <DialogContent dividers sx={{paddingBlock: 4}}>
                    <DialogContentText variant={'body2'} sx={{marginBlockEnd: 3}}>
                        Please select the external product that you want us to import.
                    </DialogContentText>
                    {
                        hasNoIndustries &&
                        <p className={'text-sm text-color-3 font-weight-semi-bold text-center my-5'}>
                            There are no industries to create a product for. Please contact the system administrator.
                        </p>
                    }
                    {
                        !hasNoIndustries && loadingIndustries &&
                        <LoadingLottieAnimation width={'80%'}/>
                    }
                    {
                        !hasNoIndustries && !loadingIndustries &&
                        <Form
                            initialValues={initialValues}
                            onSubmit={submitForm}
                            validate={validate}
                            formProps={{id: 'import-product-dialog-form'}}
                        >
                            <Grid2 spacing={2} container>
                                {
                                    industries.length > 1 &&
                                    <Grid2 xs={12}>
                                        <MuiRFF.TextField
                                            fullWidth
                                            label={'Industry'}
                                            name={'industry'}
                                            required={required.industry}
                                            select
                                            SelectProps={{
                                                renderValue: (industry) => industry?.title ?? '',
                                            }}
                                        >
                                            {
                                                industries.map(industry => (
                                                    <MenuItem key={industry.id} value={industry}>
                                                        {industry.title}
                                                    </MenuItem>
                                                ))
                                            }
                                        </MuiRFF.TextField>
                                    </Grid2>
                                }
                                <Grid2 xs={12}>
                                    <ExternalPartAutocomplete
                                        form
                                        name={'externalProduct'}
                                        label={'External Product'}
                                        required={required.externalProduct}
                                    />
                                </Grid2>
                            </Grid2>
                        </Form>
                    }
                </DialogContent>
                <DialogActions className={'d-flex align-items-center justify-content-between p-0'}>
                    <Button
                        disabled={!open || importing}
                        variant={'dialog'}
                        color={'primary'}
                        onClick={() => setOpen(false)}
                        borderRadius={'0 8px 0 8px'}
                    >
                        Close
                    </Button>
                    <Button
                        disabled={!open || importing}
                        variant={'dialog'}
                        color={'primary'}
                        form={'import-product-dialog-form'}
                        type={'submit'}
                        borderRadius={'8px 0 8px 0'}
                    >
                        {
                            importing
                                ? "Importing..."
                                : "Import"
                        }
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    )
}

export default ImportExternalProductDialog;
